/**
 * 헤더 지역 선택기 — 클라이언트에서도 쓰는 순수 부분(경로 매칭·placeholder). 서버 전용 import 없음.
 * 데이터 생성은 ./header-region.ts (서버·layout).
 */
import type { PickerTop } from '@/components/ds/v3/RegionPickerClient';

export interface HeaderRegionData {
  tops: PickerTop[];
  /** [디코드된 경로, 지역 코드] — 앞선 항목이 이긴다(시·군이 시/도보다 먼저). */
  paths: [string, string][];
  /**
   * L5 perf — true 면 paths 는 «tops/children 의 href 로 못 푸는 항목만»(별칭·재개발 허브·우선순위가 갈리는 키).
   * 나머지는 클라이언트가 href 로 다시 푼다(derivedCode) — 서버가 같은 결과를 검증해 넣은 것만 뺀다. 생략/false = 종전(전부 명시).
   */
  compact?: boolean;
}

export const EMPTY_HEADER_REGION: HeaderRegionData = { tops: [], paths: [] };

/**
 * L5 perf — href 에서 바로 푸는 경로→코드(compact 모드). 시·군(/apt/area/…) 먼저, 그다음 시/도 허브. '/apt'·'/apt/redev' 는 지역이 아니다.
 * 서버(buildHeaderRegionData)가 같은 함수로 «명시 목록에서 뺄 수 있는 항목» 을 고르므로 두 쪽이 같은 답을 낸다.
 */
export function derivedCode(p: string, tops: readonly PickerTop[]): string | null {
  if (!p || p === '/apt' || p === '/apt/redev') return null;
  if (p.startsWith('/apt/area/')) {
    for (const t of tops) for (const c of t.children) if (normPath(c.href) === p) return c.code;
  }
  for (const t of tops) if (normPath(t.href) === p) return t.code;
  return null;
}

/** 경로 → 지역 코드(명시 목록 먼저 · compact 면 href 유도 보충). 없으면 null. */
export function headerRegionCode(p: string, data: HeaderRegionData): string | null {
  const hit = data.paths.find(([k]) => k === p);
  if (hit) return hit[1];
  return data.compact ? derivedCode(p, data.tops) : null;
}

/** URL 경로 정규화: 퍼센트 디코드(실패 시 원문) · 끝 슬래시 제거. 한글 slug 가 인코드/디코드 두 형태로 온다. */
export function normPath(p: string | null | undefined): string {
  let s = String(p ?? '').split(/[?#]/)[0];
  try { s = decodeURIComponent(s); } catch { /* 깨진 인코딩 — 원문 */ }
  if (s.length > 1) s = s.replace(/\/+$/, '');
  return s;
}

/** 경로 → 현재 지역 trail(상위부터). 허브 경로가 아니면 [] (= 전국). */
export function matchHeaderRegion(pathname: string | null | undefined, data: HeaderRegionData | null | undefined): { code: string; label: string }[] {
  if (!data || !data.tops.length) return [];
  const p = normPath(pathname);
  if (!p.startsWith('/apt/')) return [];
  const code = headerRegionCode(p, data);
  if (!code) return [];
  for (const t of data.tops) {
    if (t.code === code) return [{ code: t.code, label: t.label }];
    const c = t.children.find((k) => k.code === code);
    if (c) return [{ code: t.code, label: t.label }, { code: c.code, label: c.label }];
  }
  return [];
}

/** 검색창 placeholder — 지역을 따른다. 전국이면 기본 문구(fallback). */
export function headerSearchPlaceholder(regionName: string | null | undefined, fallback: string): string {
  const n = (regionName ?? '').trim();
  return n ? `${n} 단지명·지역 검색` : fallback;
}
